import { Controller } from "stimulus"
import Rails from "@rails/ujs";

// PaymentKit.js card collection — the PaymentKit analogue of stripe_controller.js
// / payment_method_controller.js. Used on BOTH the signup payment page and the
// in-app billing "payment method" page (data-controller="payment-kit").
//
// Instead of a Stripe SetupIntent we fetch a checkout-session `secure_token`
// from the server, render PaymentKit.js card iframes, then submit the card
// against that token. On success the hidden session/intent ids (if present on
// the form) are written and the Rails form is submitted.
export default class extends Controller {

  static targets = ['form']

  connect() {
    if (typeof PaymentKit === 'undefined') {
      console.error('PaymentKit.js failed to load.');
      toastr.error('Payment form failed to load. Please refresh and try again.');
      return;
    }

    var form = this.formTarget;
    var prefix = form.getAttribute('data-prefix');
    var accountId = form.getAttribute('data-account-id');
    var customerEmail = form.getAttribute('data-customer-email');
    // 'setup' (in-app card update, no subscription) vs 'payment' (signup trial).
    // Absent on the signup form, where the server defaults to 'payment'.
    var sessionMode = form.getAttribute('data-session-mode');
    var cardButton = document.getElementById(prefix + '-submit');
    var controller = this;
    // Stash the field prefix so the validation helpers can resolve the card
    // number container (the exp/cvc containers have fixed ids).
    this.prefix = prefix;

    // Validetta validates the surrounding billing inputs that ARE real DOM fields:
    // the name fields (signup) and the Country + ZIP fields (billing page). The
    // PaymentKit card inputs are PCI iframes whose contents validetta cannot read,
    // so their validity — including CVC length per the card brand PaymentKit detects
    // inside the card-number iframe — is enforced by PaymentKit itself and surfaced
    // at submit time via onError -> showCardFieldErrors().
    var validettaFields = form.querySelectorAll('[data-validetta]');
    var hasValidettaFields = validettaFields.length > 0;
    var billingDetails = hasValidettaFields ? "error" : "complete";

    if (hasValidettaFields) {
      $(form).validetta({
        showErrorMessages : true,
        display : 'bubble',
        errorTemplateClass : 'validetta-bubble',
        errorClass : 'validetta-error',
        validClass : 'validetta-valid',
        bubblePosition: 'bottom',
        bubbleGapLeft: 15,
        bubbleGapTop: 9,
        realTime : true,
        validators: {
          regExp: {
            validName: {
              pattern: /^[A-Za-z\s]+$/,
              errorMessage: "Please enter a valid name with only alphabets."
            },
            // Permissive international postcode / ZIP: starts alphanumeric, then
            // 1-9 more letters/digits/spaces/hyphens (covers US, UK, CA, etc.).
            validZip: {
              pattern: /^[A-Za-z0-9][A-Za-z0-9\s\-]{1,9}$/,
              errorMessage: "Please enter a valid postcode / ZIP."
            }
          }
        },
        onValid : function(){ billingDetails = "complete"; },
        onError : function(){ billingDetails = "error"; }
      });
    }

    // FETCH A CHECKOUT SESSION (secure_token ~ Stripe client_secret)
    Rails.ajax({
      type: "post",
      url: '/payment_kit/session/' + accountId + '.json',
      data: sessionMode ? ('mode=' + encodeURIComponent(sessionMode)) : '',
      success: function(data) {
        if (!data || !data.secure_token) {
          toastr.error((data && data.error) || 'Could not start payment session.');
          return;
        }

        var paymentKit = PaymentKit.default({
          environment: data.environment,
          secureToken: data.secure_token,
          paymentMethods: [
            PaymentKit.PaymentMethods.card,
            PaymentKit.PaymentMethods.applePay,
            PaymentKit.PaymentMethods.googlePay
          ]
        });
        controller.paymentKit = paymentKit;

        // CREATE + MOUNT CARD IFRAMES (elements live under paymentKit.card)
        var style = {
          fontSize: '16px',
          fontFamily: '"Circular Std", Helvetica, sans-serif',
          color: '#2c3e50',
          fontSmoothing: 'antialiased',
          fontWeight: '400',
        };

        paymentKit.card.createElement('card_pan', { style: style, placeholder: '1234 1234 1234 1234' }).mount('#' + prefix + '-element');
        paymentKit.card.createElement('card_exp', { style: style, placeholder: 'MM / YY' }).mount('#card-expiry-element');
        paymentKit.card.createElement('card_cvc', { style: style, placeholder: '***' }).mount('#card-cvc-element');

        // EXPRESS WALLETS (Google Pay / Apple Pay) — render the official branded
        // buttons (PaymentKit.js has no built-in wallet button) and reveal them
        // only once the SDK reports the wallet is ready for this checkout session.
        controller.setupWallets(paymentKit, data.environment);

        // WAIT FOR SUBMISSION
        form.addEventListener('submit', function(event) {
          // PREVENT NORMAL SUBMIT
          $(cardButton).prop('disabled', true);
          event.preventDefault();

          if (billingDetails === "error") {
            // Validetta has already flagged the offending name / country / ZIP
            // field(s) inline; surface a generic prompt as a secondary cue.
            $(cardButton).prop('disabled', false)
            toastr.error("Please complete the highlighted billing fields.");
            cardButton.blur();
          } else if (billingDetails === "complete") {
            // Clear any stale per-field card errors before re-validating.
            controller.clearCardFieldErrors();
            paymentKit.submit({
              paymentMethod: 'card',
              fields: {
                customer_email: customerEmail,
                customer_name: controller.cardholderName(form),
                customer_country: controller.customerField('card-country', form, 'data-card-country'),
                customer_zip_code: controller.customerField('card-zip', form, 'data-card-zip')
              },
              // The signup form asks for no country or postcode (like Stripe's), but
              // PaymentKit.js requires both unless its customer validation is skipped.
              // Name and email are still sent; PaymentKit takes the billing address as optional.
              options: document.getElementById('card-country') ? undefined : { skipCustomerValidation: true },
              onSuccess: function(result) {
                // HANDLE SUCCESS
                controller.clearCardFieldErrors();
                controller.setValue('payment_kit_checkout_session_id', result.checkoutSessionId);
                controller.setValue('payment_intent_id', result.paymentIntentId);
                form.submit();
              },
              onError: function(errors) {
                // HANDLE ERRORS — surface PaymentKit's field-level card validation
                // (card_pan / card_exp / card_cvc) inline on each iframe, and fall
                // back to a toast for general / non-card errors.
                $(cardButton).prop('disabled', false)
                var cardFieldShown = controller.showCardFieldErrors(errors);
                var general = controller.generalError(errors);
                if (general) {
                  toastr.error(general);
                } else if (!cardFieldShown) {
                  toastr.error("Please finish entering your card details.");
                }
                cardButton.blur();
              }
            });
          }
        });
      },
      error: function() {
        toastr.error('Could not start payment session. Please try again.');
      }
    });
  }

  disconnect() {
    if (this.paymentKit && typeof this.paymentKit.cleanup === 'function') {
      this.paymentKit.cleanup();
    }
  }

  // Signup form derives the name from first/last inputs; billing passes it as a
  // data attribute.
  cardholderName(form) {
    var firstName = document.getElementById('first-name');
    var lastName = document.getElementById('last-name');
    if (firstName && lastName) { return (firstName.value + ' ' + lastName.value).trim(); }
    return form.getAttribute('data-cardholder-name') || '';
  }

  setValue(id, value) {
    var el = document.getElementById(id);
    if (el) { el.setAttribute('value', value || ''); }
  }

  // Reads a customer field (country / ZIP) from its form input, falling back to
  // the server-provided data attribute when the input is absent or empty.
  customerField(inputId, form, attr) {
    var el = document.getElementById(inputId);
    if (el && el.value) { return el.value.trim(); }
    return form.getAttribute(attr) || '';
  }

  // Resolve the field-keyed error object PaymentKit.js returns to onError into a
  // single message, mirroring how stripe_controller surfaces one toastr error:
  // card-field problems reuse the same "finish entering your card details"
  // message, and a general error shows its own message.
  errorMessage(errors) {
    if (!errors) { return "Please finish entering your card details."; }
    if (typeof errors === 'string') { return errors; }
    if (errors.root) { return errors.root; }
    if (errors.google_pay) { return errors.google_pay; }
    if (errors.apple_pay) { return errors.apple_pay; }
    return "Please finish entering your card details.";
  }

  // Map each card iframe to the PaymentKit error field that validates it.
  cardFieldMap() {
    return {
      card_pan: this.prefix + '-element',
      card_exp: 'card-expiry-element',
      card_cvc: 'card-cvc-element'
    };
  }

  // Human-readable label per card field, used to build the inline message.
  cardFieldLabel(field) {
    return {
      card_pan: 'card number',
      card_exp: 'expiry date',
      card_cvc: 'security code'
    }[field] || 'card details';
  }

  // Turn a PaymentKit field code (required / invalid / unknown_error) into a
  // customer-facing message for the given card field.
  cardFieldMessage(field, code) {
    var label = this.cardFieldLabel(field);
    if (code === 'required') { return 'Your ' + label + ' is required.'; }
    if (code === 'invalid') { return 'Your ' + label + ' is invalid.'; }
    return 'Please check your ' + label + ' and try again.';
  }

  // Render PaymentKit's per-field card errors inline under each iframe. Returns
  // true if at least one card-field error was shown.
  showCardFieldErrors(errors) {
    if (!errors || typeof errors !== 'object') { return false; }

    var map = this.cardFieldMap();
    var shown = false;
    var controller = this;
    Object.keys(map).forEach(function(field) {
      var code = errors[field];
      if (code) {
        controller.showFieldError(map[field], controller.cardFieldMessage(field, code));
        shown = true;
      } else {
        controller.clearFieldError(map[field]);
      }
    });
    return shown;
  }

  // Show a message beneath a card iframe container (the error node is created on
  // demand, so no view changes are required) and flag the container invalid.
  showFieldError(containerId, message) {
    var container = document.getElementById(containerId);
    if (!container) { return; }
    container.classList.add('pk-field-invalid');

    var errorId = containerId + '-error';
    var errorEl = document.getElementById(errorId);
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.id = errorId;
      // Reuse validetta's own inline error style so card-field messages match the
      // name / country / ZIP validation (red #cd2e26, Circular Std 13px). The
      // inline color is kept as a fallback in case the stylesheet is absent.
      errorEl.className = 'pk-field-error validetta-inline';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.color = '#cd2e26';
      errorEl.style.marginTop = '4px';
      container.parentNode.insertBefore(errorEl, container.nextSibling);
    }
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }

  clearFieldError(containerId) {
    var container = document.getElementById(containerId);
    if (container) { container.classList.remove('pk-field-invalid'); }
    var errorEl = document.getElementById(containerId + '-error');
    if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
  }

  clearCardFieldErrors() {
    var map = this.cardFieldMap();
    var controller = this;
    Object.keys(map).forEach(function(field) { controller.clearFieldError(map[field]); });
  }

  // Non-card error to surface as a toast (general failure / email / wallets).
  generalError(errors) {
    if (!errors || typeof errors !== 'object') { return null; }
    if (errors.root) { return errors.root; }
    if (errors.customer_email === 'required') { return 'Please enter your email address.'; }
    if (errors.customer_email === 'invalid') { return 'Please enter a valid email address.'; }
    if (errors.google_pay) { return errors.google_pay; }
    if (errors.apple_pay) { return errors.apple_pay; }
    return null;
  }

  // Render the official wallet buttons into their containers and reveal them
  // only when the SDK reports the wallet is ready (account has the wallet
  // configured + device/browser supports it).
  setupWallets(paymentKit, environment) {
    this.setupGooglePay(paymentKit, environment);
    this.setupApplePay(paymentKit);
  }

  // Show the wallet section (the "Or Pay with" divider plus the button row) only
  // while at least one wallet button is visible, so neither an unsupported browser
  // nor a failed button render leaves a separator hanging over empty space.
  syncWalletSection() {
    var section = document.getElementById('payment-kit-wallets');
    if (!section) { return; }

    section.hidden = !['apple-pay-button', 'google-pay-button'].some(function(id) {
      var button = document.getElementById(id);
      return button && !button.hidden;
    });
  }

  // Google's official button via google.payments.api (from pay.js).
  setupGooglePay(paymentKit, environment) {
    var wallet = paymentKit.google_pay;
    var container = document.getElementById('google-pay-button');
    if (!wallet || !container || typeof wallet.onGooglePayReady !== 'function') { return; }

    var controller = this;
    var rendered = false;

    wallet.onGooglePayReady(function(isReady) {
      container.hidden = !isReady;
      controller.syncWalletSection();
      if (!isReady || rendered) { return; }
      if (!(window.google && window.google.payments && window.google.payments.api)) { return; }

      rendered = true;
      try {
        var client = new google.payments.api.PaymentsClient({
          environment: environment === 'production' ? 'PRODUCTION' : 'TEST'
        });
        var button = client.createButton({
          buttonSizeMode: 'fill',
          buttonType: 'plain',
          onClick: function() { controller.submitWallet(paymentKit, 'google_pay'); }
        });
        container.innerHTML = '';
        container.appendChild(button);
      } catch (e) {
        console.error('Google Pay button render failed', e);
        container.hidden = true;
        controller.syncWalletSection();
      }
    });
  }

  // Apple's official <apple-pay-button> web component (from apple-pay-sdk.js).
  setupApplePay(paymentKit) {
    var wallet = paymentKit.apple_pay;
    var container = document.getElementById('apple-pay-button');
    if (!wallet || !container || typeof wallet.onApplePayReady !== 'function') { return; }

    var controller = this;
    var rendered = false;

    wallet.onApplePayReady(function(isReady) {
      container.hidden = !isReady;
      controller.syncWalletSection();
      if (!isReady || rendered) { return; }

      rendered = true;
      container.innerHTML = '<apple-pay-button buttonstyle="black" type="plain" locale="en-US"></apple-pay-button>';
      var button = container.querySelector('apple-pay-button');
      if (button) {
        button.addEventListener('click', function() { controller.submitWallet(paymentKit, 'apple_pay'); });
      }
    });
  }

  submitWallet(paymentKit, method) {
    var controller = this;
    paymentKit.submit({
      fields: {},
      paymentMethod: method,
      options: { customerInfo: controller.customerInfo() },
      onSuccess: function(result) { controller.onWalletSuccess(result); },
      onError: function(errors) { toastr.error(controller.errorMessage(errors)); }
    });
  }

  // Google Pay / Apple Pay return slightly different result shapes than the card
  // flow; persist whatever checkout-session / payment ids are present, then submit.
  onWalletSuccess(result) {
    this.setValue('payment_kit_checkout_session_id', result.checkoutSessionId || result.checkout_session_id);
    this.setValue('payment_intent_id', result.paymentIntentId || result.id || result.transaction_id);
    this.formTarget.submit();
  }

  // Optional payer name for the wallet sheet (the sheet supplies the final name).
  customerInfo() {
    var firstName = document.getElementById('first-name');
    var lastName = document.getElementById('last-name');
    if (firstName && lastName) {
      return { first_name: firstName.value.trim(), last_name: lastName.value.trim() };
    }

    var parts = (this.formTarget.getAttribute('data-cardholder-name') || '').trim().split(/\s+/);
    return { first_name: parts[0] || '', last_name: parts.slice(1).join(' ') };
  }
}
